import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { categorySeoContent } from '@/data/categorySeoContent';
import { Search, SlidersHorizontal, Grid, List, X } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CartDrawer from '@/components/CartDrawer';
import ProductCard from '@/components/ProductCard';
import SEO from '@/components/SEO';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { productsStore, categoriesStore, initializeCache, Product, Category } from '@/stores/localStorageStore';
import { productImages } from '@/lib/images';
import ProductImage from '@/components/ProductImage';
import { generateSlug } from '@/lib/utils';

const ProductsPage = () => {
  const { language } = useLanguage();
  const { isCartOpen, setIsCartOpen } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { categorySlug } = useParams();

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);

  const slugToCategoryMap: Record<string, string> = {
    'wina': 'wines',
    'oliwa': 'italianProducts',
    'tradycyjne-przekaski': 'tradycyjne-przekaski',
    'zestawy': 'gifts',
    'pozostale-produkty': 'pozostale-produkty',
    'pozostale': 'pozostale-produkty'
  };

  const activeCategory = categorySlug ? (slugToCategoryMap[categorySlug] || categorySlug) : '';
  const categoryFilter = activeCategory || searchParams.get('category') || '';
  const subcategoryFilter = searchParams.get('subcategory') || '';
  const searchQuery = searchParams.get('search') || '';
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 500]);
  const [localSearch, setLocalSearch] = useState(searchQuery);

  useEffect(() => {
    const loadData = async () => {
      try {
        await initializeCache();
        setProducts(productsStore.getAll());
        setCategories(categoriesStore.getAll());
      } catch (error) {
        console.error('Failed to load products:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);

  // Debounced live search — aktualizuje URL params automatycznie po 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      if (localSearch) {
        newParams.set('search', localSearch);
      } else {
        newParams.delete('search');
      }
      // Aktualizuj tylko jeśli się zmieniło
      if (newParams.get('search') !== searchParams.get('search')) {
        setSearchParams(newParams);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch]);

  const filteredProducts = useMemo(() => {
    const filtered = products.filter(product => {
      if (categoryFilter && product.category !== categoryFilter) return false;
      if (subcategoryFilter && product.subcategory !== subcategoryFilter) return false;
      if (product.price < priceRange[0] || product.price > priceRange[1]) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const nameMatch = product.name.toLowerCase().includes(query);
        const descMatch = product.description?.toLowerCase().includes(query);
        const descEnMatch = product.descriptionEn?.toLowerCase().includes(query);
        if (!nameMatch && !descMatch && !descEnMatch) return false;
      }
      if (product.status !== 'active' && product.status !== 'out_of_stock') return false;
      return true;
    });

    // Sort products: stock > 0 first, stock === 0 last
    return [...filtered].sort((a, b) => {
      const aOutOfStock = (a.stock ?? 0) <= 0 || a.status === 'out_of_stock';
      const bOutOfStock = (b.stock ?? 0) <= 0 || b.status === 'out_of_stock';
      if (!aOutOfStock && bOutOfStock) return -1;
      if (aOutOfStock && !bOutOfStock) return 1;
      return 0;
    });
  }, [products, categoryFilter, subcategoryFilter, priceRange, searchQuery]);

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const getCategoryUrl = (category: string) => {
    const seoMap: Record<string, string> = {
        'wines': 'wina',
        'tradycyjne-przekaski': 'tradycyjne-przekaski',
        'italianProducts': 'oliwa',
        'coffee': 'kawa',
        'gifts': 'zestawy',
        'accessories': 'akcesoria',
        'pozostale-produkty': 'pozostale-produkty'
    };
    const mapped = seoMap[category];
    return mapped ? `/produkty/${mapped}` : `/produkty/${category}`;
  };

  const handleCategoryChange = (categoryName: string) => {
    if (categoryName) {
      navigate(getCategoryUrl(categoryName));
    } else {
      navigate('/produkty');
    }
  };

  const clearFilters = () => {
    setSearchParams({});
    setPriceRange([0, 500]);
    setLocalSearch('');
    if (categorySlug) {
      navigate('/produkty');
    }
  };

  const isPriceChanged = priceRange[0] !== 0 || priceRange[1] !== 500;
  const activeFiltersCount = [categoryFilter, subcategoryFilter, searchQuery].filter(Boolean).length + (isPriceChanged ? 1 : 0);
  const currentCategory = categories.find(c => c.name === categoryFilter);

  const seoData = activeCategory ? categorySeoContent[activeCategory] : null;

  const pageTitle = seoData 
    ? seoData.metaTitle
    : currentCategory 
      ? `${language === 'EN' && currentCategory.displayNameEn ? currentCategory.displayNameEn : currentCategory.displayName} - Włoska piwnica`
      : (language === 'PL' ? 'Wszystkie produkty - Włoska piwnica' : 'All products - Włoska piwnica');

  const pageDescription = seoData 
    ? seoData.metaDescription
    : (language === 'PL' ? 'Poznaj naszą ofertę włoskich win, oliwy, przekąsek i innych delikatesów.' : 'Explore our offer of Italian wines, olive oil, snacks, and other delicacies.');

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemList",
        "itemListElement": filteredProducts.slice(0, 20).map((p, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "url": `https://wloska-piwnica.pl/produkty/${p.category}/${p.id}/${generateSlug(p.name)}`
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Strona główna", "item": "https://wloska-piwnica.pl/" },
          ...(currentCategory ? [
            { "@type": "ListItem", "position": 2, "name": "Produkty", "item": "https://wloska-piwnica.pl/produkty" },
            { "@type": "ListItem", "position": 3, "name": currentCategory.displayName }
          ] : [
            { "@type": "ListItem", "position": 2, "name": "Produkty" }
          ])
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={categorySlug ? `/produkty/${categorySlug}` : "/produkty"}
        jsonLd={jsonLd}
      />
      <Header onCartClick={() => setIsCartOpen(true)} />


      <main className="container mx-auto px-4 py-8 pt-36 md:pt-40 lg:pt-44">
        <div className="mb-8">
          <nav className="mb-4 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary">{language === 'PL' ? 'Strona główna' : 'Home'}</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{language === 'PL' ? 'Produkty' : 'Products'}</span>
            {currentCategory && (
              <>
                <span className="mx-2">/</span>
                <span className="text-foreground">{language === 'EN' && currentCategory.displayNameEn ? currentCategory.displayNameEn : currentCategory.displayName}</span>
              </>
            )}
            {subcategoryFilter && (
              <>
                <span className="mx-2">/</span>
                <span className="text-foreground">{subcategoryFilter}</span>
              </>
            )}
          </nav>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="w-full lg:w-3/4">
              <h1 className="font-display text-3xl font-bold lg:text-4xl text-primary">
                {seoData ? seoData.h1 : (currentCategory ? (language === 'EN' && currentCategory.displayNameEn ? currentCategory.displayNameEn : currentCategory.displayName) : (language === 'PL' ? 'Wszystkie produkty' : 'All products'))}
              </h1>
              {seoData && seoData.seoText && (
                <p className="mt-3 text-muted-foreground text-sm md:text-base leading-relaxed">
                  {seoData.seoText}
                </p>
              )}
              <p className="mt-2 text-muted-foreground text-sm">
                {filteredProducts.length} {language === 'PL' ? 'produktów' : 'products'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:bg-muted lg:hidden">
                <SlidersHorizontal className="h-4 w-4" />
                {language === 'PL' ? 'Filtry' : 'Filters'}
                {activeFiltersCount > 0 && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              <div className="hidden items-center gap-1 rounded-lg border border-border bg-card p-1 sm:flex">
                <button onClick={() => setViewMode('grid')}
                  className={`rounded-md p-2 ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                  <Grid className="h-4 w-4" />
                </button>
                <button onClick={() => setViewMode('list')}
                  className={`rounded-md p-2 ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          <aside className={`fixed inset-y-0 left-0 z-50 w-72 transform overflow-y-auto bg-card p-6 pt-20 shadow-xl transition-transform lg:static lg:z-auto lg:block lg:w-64 lg:h-fit lg:self-start lg:transform-none lg:pt-0 lg:shadow-none ${showFilters ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
            <div className="flex items-center justify-between lg:hidden">
              <h2 className="font-semibold">{language === 'PL' ? 'Filtry' : 'Filters'}</h2>
              <button onClick={() => setShowFilters(false)} className="rounded-lg p-2 hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 space-y-6 lg:mt-0">
              <div>
                <label className="mb-2 block text-sm font-medium">{language === 'PL' ? 'Szukaj' : 'Search'}</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input type="text" value={localSearch} onChange={(e) => setLocalSearch(e.target.value)}
                    placeholder={language === 'PL' ? 'Nazwa produktu...' : 'Product name...'}
                    className="w-full rounded-lg border border-border bg-background py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none" />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">{language === 'PL' ? 'Kategoria' : 'Category'}</label>
                <div className="space-y-1">
                  <Link to="/produkty"
                    className={`block w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${!categoryFilter ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                    {language === 'PL' ? 'Wszystkie' : 'All'}
                  </Link>
                  {categories.filter(c => c.name !== 'pos').map(category => (
                    <Link key={category.id} to={getCategoryUrl(category.name)}
                      className={`block w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${categoryFilter === category.name ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                      {language === 'EN' && category.displayNameEn ? category.displayNameEn : category.displayName}
                    </Link>
                  ))}
                </div>
              </div>

              {currentCategory?.subcategories && currentCategory.subcategories.length > 0 && (
                <div>
                  <label className="mb-2 block text-sm font-medium">{language === 'PL' ? 'Podkategoria' : 'Subcategory'}</label>
                  <div className="space-y-1">
                    <button onClick={() => updateFilter('subcategory', '')}
                      className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${!subcategoryFilter ? 'bg-primary/80 text-primary-foreground' : 'hover:bg-muted'}`}>
                      {language === 'PL' ? 'Wszystkie' : 'All'}
                    </button>
                    {currentCategory.subcategories.map(sub => (
                      <button key={sub} onClick={() => updateFilter('subcategory', sub)}
                        className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${subcategoryFilter === sub ? 'bg-primary/80 text-primary-foreground' : 'hover:bg-muted'}`}>
                        {sub}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Types filter removed */}

              <div>
                <label className="mb-2 block text-sm font-medium">
                  {language === 'PL' ? 'Cena' : 'Price'}: {priceRange[0]} - {priceRange[1]} zł
                </label>
                <div className="space-y-2">
                  <input type="range" min="0" max="500" step="10" value={priceRange[0]}
                    onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                    className="w-full accent-primary" />
                  <input type="range" min="0" max="500" step="10" value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                    className="w-full accent-primary" />
                </div>
              </div>

              {activeFiltersCount > 0 && (
                <button onClick={clearFilters}
                  className="w-full rounded-lg border border-border py-2 text-sm font-medium transition-colors hover:bg-muted">
                  {language === 'PL' ? 'Wyczyść filtry' : 'Clear filters'}
                </button>
              )}
            </div>
          </aside>

          {showFilters && (
            <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setShowFilters(false)} />
          )}

          <div className="flex-1">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                <p className="text-muted-foreground">
                  {language === 'PL' ? 'Ładowanie produktów...' : 'Loading products...'}
                </p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Search className="mb-4 h-16 w-16 text-muted-foreground/30" />
                <h3 className="mb-2 text-xl font-semibold">
                  {language === 'PL' ? 'Pracujemy jeszcze nad tą ofertą dla ciebie' : 'We are still working on this offer for you'}
                </h3>
                <button onClick={clearFilters} className="mb-4 text-sm font-medium text-muted-foreground hover:text-primary hover:underline underline-offset-4 transition-all">
                  {language === 'PL' ? 'Zobacz nasze inne produkty' : 'See our other products'}
                </button>
                <button onClick={clearFilters} className="rounded-lg bg-primary px-6 py-2 font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
                  {language === 'PL' ? 'Wyczyść filtry' : 'Clear filters'}
                </button>
              </div>
            ) : (
              <motion.div layout
                className={viewMode === 'grid'
                  ? 'grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                  : 'space-y-4'}>
                <AnimatePresence mode="popLayout">
                  {filteredProducts.map((product) => (
                    <motion.div key={product.id} layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2 }}>
                      {viewMode === 'grid' ? (
                        <ProductCard
                          id={product.id}
                          name={product.name}
                          price={product.price}
                          description={product.description || ''}
                          descriptionEn={product.descriptionEn || ''}
                          image={product.image || (product.images && product.images[0]) || 'primitivo'}
                          stock={product.stock}
                        />
                      ) : (
                        <Link to={`/produkty/${product.category}/${product.id}/${generateSlug(product.name)}`}
                          className="flex gap-4 rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-lg">
                          <div className="h-32 w-24 flex-shrink-0 overflow-hidden rounded-lg bg-muted">
                            <ProductImage src={product.image?.startsWith('/api/images/uploads/') ? product.image : (productImages[product.image as keyof typeof productImages] || product.image)}
                              alt={product.name} loading="lazy" className="h-full w-full object-cover" />
                          </div>
                          <div className="flex flex-1 flex-col justify-between">
                            <div>
                              <span className="text-xs font-medium uppercase text-primary">{product.subcategory || product.category}</span>
                              <h3 className="font-semibold">{product.name}</h3>
                              <p className="line-clamp-2 text-sm text-muted-foreground">{language === 'EN' && product.descriptionEn ? product.descriptionEn : product.description}</p>
                            </div>
                            <p className="font-display text-lg font-bold text-primary whitespace-nowrap">{product.price} zł</p>
                          </div>
                        </Link>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
        </div>

        {/* FAQ Sekcja SEO na samym dole pod listą produktów */}
        {seoData && seoData.faq && seoData.faq.length > 0 && (
          <section className="mt-16 bg-muted/30 p-6 md:p-8 rounded-2xl w-full">
            <h2 className="font-display text-2xl font-bold mb-6 text-foreground">
              Często zadawane pytania ({seoData.h1.split('—')[0].trim()})
            </h2>
            <div className="grid gap-6">
              {seoData.faq.map((item, index) => (
                <div key={index} className="bg-card p-6 rounded-xl shadow-sm border border-border">
                  <h3 className="font-bold text-lg mb-2 text-primary">{item.q}</h3>
                  <p className="text-muted-foreground">{item.a}</p>
                </div>
              ))}
            </div>
            
            <script type="application/ld+json" dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": seoData.faq.map(item => ({
                  "@type": "Question",
                  "name": item.q,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": item.a
                  }
                }))
              })
            }} />
          </section>
        )}
      </main>

      <Footer />

      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
      />
    </div>
  );
};

export default ProductsPage;
